import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { orderAPI, couponAPI, activityAPI, cartRemovalCouponAPI } from '../services/api';
import { getActivitySessionId } from '../utils/activitySession';
import { useAuth } from './AuthContext';

// Cart Context
const CartContext = createContext();

// Cart Actions
const CART_ACTIONS = {
  ADD_TO_CART: 'ADD_TO_CART',
  REMOVE_FROM_CART: 'REMOVE_FROM_CART',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  APPLY_COUPON: 'APPLY_COUPON',
  REMOVE_COUPON: 'REMOVE_COUPON'
};

// Cart Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_TO_CART:
      const existingItem = state.items.find(item => item.id === action.payload.id);
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      }
      
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case CART_ACTIONS.REMOVE_FROM_CART:
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case CART_ACTIONS.UPDATE_QUANTITY:
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item
        ).filter(item => item.quantity > 0)
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: []
      };

    case CART_ACTIONS.APPLY_COUPON:
      return {
        ...state,
        appliedCoupon: action.payload.coupon,
        discountAmount: action.payload.discountAmount
      };

    case CART_ACTIONS.REMOVE_COUPON:
      return {
        ...state,
        appliedCoupon: null,
        discountAmount: 0
      };

    default:
      return state;
  }
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  // Initialize state from localStorage if available
  const getInitialState = () => {
    if (typeof window !== 'undefined') {
      const savedCart = localStorage.getItem('anadolu-cart');
      if (savedCart) {
        try {
          const cartData = JSON.parse(savedCart);
          return {
            items: Array.isArray(cartData) ? cartData : [],
            appliedCoupon: null,
            discountAmount: 0
          };
        } catch (error) {
          console.error('Sepet verisi yüklenirken hata:', error);
          localStorage.removeItem('anadolu-cart');
        }
      }
    }
    return {
      items: [],
      appliedCoupon: null,
      discountAmount: 0
    };
  };

  const [state, dispatch] = useReducer(cartReducer, getInitialState());

  // Save cart to localStorage whenever items change
  useEffect(() => {
    if (state.items.length > 0) {
      localStorage.setItem('anadolu-cart', JSON.stringify(state.items));
    } else {
      // Clear localStorage if cart is empty
      localStorage.removeItem('anadolu-cart');
    }
  }, [state.items]);

  // Cart Actions
  const addToCart = (product, quantity = 1) => {
    // Block out-of-stock products
    const inStock = product.inStock !== undefined
      ? product.inStock
      : (product.stockQuantity == null || product.stockQuantity > 0);
    if (!inStock) return;

    dispatch({
      type: CART_ACTIONS.ADD_TO_CART,
      payload: {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.mainImageUrl || product.image,
        description: product.description,
        quantity: quantity,
        taxRate: product.taxRate || 21
      }
    });

    // Müşteri tercih takibi: sepete ekleme sinyali
    activityAPI.track(getActivitySessionId(), product.id, 'ADD_TO_CART').catch(() => {});
  };

  const removeFromCart = (productId) => {
    dispatch({
      type: CART_ACTIONS.REMOVE_FROM_CART,
      payload: productId
    });

    // Sepetten çıkarma kuponu: yalnızca giriş yapmış kullanıcılar için değerlendirilir,
    // gönderilip gönderilmeyeceğine (özellik açık mı, cooldown doldu mu) backend karar verir.
    if (user?.id) {
      cartRemovalCouponAPI.trigger().catch(() => {});
    }
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { id: productId, quantity }
    });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  // Cart Calculations
  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    const subtotal = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    return subtotal - state.discountAmount;
  };

  const getSubtotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTaxAmount = () => {
    // Calculate tax for each item
    let totalTax = 0;
    state.items.forEach(item => {
      const itemTotal = item.price * item.quantity;
      const taxRate = item.taxRate || 21; // Default 21% tax
      const itemTax = (itemTotal * taxRate) / 100;
      totalTax += itemTax;
    });
    return totalTax;
  };

  const getDiscountAmount = () => {
    return state.discountAmount;
  };

  const getAppliedCoupon = () => {
    return state.appliedCoupon;
  };

  // Apply coupon
  const applyCoupon = async (couponCode) => {
    try {
      const subtotal = getSubtotal();
      const response = await couponAPI.validateCoupon(couponCode, subtotal);
      
      if (response.data.valid) {
        const discountResponse = await couponAPI.calculateDiscount(couponCode, subtotal);
        const discountAmount = discountResponse.data.discount;
        
        dispatch({
          type: CART_ACTIONS.APPLY_COUPON,
          payload: {
            coupon: { code: couponCode },
            discountAmount: discountAmount
          }
        });
        
        return { success: true, message: 'Kupon başarıyla uygulandı!' };
      } else {
        return { success: false, message: 'Geçersiz kupon kodu!' };
      }
    } catch (error) {
      console.error('Coupon application error:', error);
      return { success: false, message: 'Kupon uygulanırken bir hata oluştu!' };
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    dispatch({ type: CART_ACTIONS.REMOVE_COUPON });
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  // Create order from cart
  const createOrder = async (orderData) => {
    try {
      const orderItems = state.items.map(item => ({
        product: { id: item.id },
        quantity: item.quantity,
        unitPrice: item.price,
        totalPrice: item.price * item.quantity,
        selectedSize: item.selectedSize || null,
        selectedFlavor: item.selectedFlavor || null
      }));

      const orderPayload = {
        ...orderData,
        items: orderItems,
        totalAmount: orderData.totalAmount != null ? orderData.totalAmount : getTotalPrice(),
        status: 'PENDING'
      };

      const response = await orderAPI.createOrder(orderPayload);
      
      // Clear cart after successful order
      clearCart();
      
      return { success: true, order: response.data };
    } catch (error) {
      console.error('Order creation error:', error);
      return { 
        success: false, 
        message: error.response?.data?.error || 'Sipariş oluşturulamadı!' 
      };
    }
  };

  const value = {
    items: state.items,
    appliedCoupon: state.appliedCoupon,
    discountAmount: state.discountAmount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getSubtotal,
    getTaxAmount,
    getDiscountAmount,
    getAppliedCoupon,
    getItemQuantity,
    isInCart,
    applyCoupon,
    removeCoupon,
    createOrder
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom Hook to use Cart Context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart hook CartProvider içinde kullanılmalıdır');
  }
  return context;
};

export default CartContext;
